import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useResetVolunteerPassword } from '@/hooks/queries';
import { validatePassword, getPasswordHelperText } from '@/utils/passwordValidation';
import type { CampaignUserDto } from '@signalvote/shared';

interface ResetPasswordDialogProps {
  volunteer: CampaignUserDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ResetResult =
  | { success: true; password?: string }
  | { success: false; error: string };

/**
 * Reset a staff member's password from the staff page.
 *
 * The API does NOT email anyone — it generates (or accepts) a password and
 * returns it in the response for the admin to relay. So this dialog has two
 * jobs: confirm the action (optionally with a custom password), then SHOW the
 * resulting password. The previous staff-page menu item fired the mutation and
 * discarded the response, so the reset silently changed the password to a value
 * nobody could see. Keep the result display — it is the whole point.
 */
export function ResetPasswordDialog({ volunteer, open, onOpenChange }: ResetPasswordDialogProps) {
  const [useCustomPassword, setUseCustomPassword] = useState(false);
  const [customPassword, setCustomPassword] = useState('');
  const [result, setResult] = useState<ResetResult | null>(null);
  const { mutate: resetPassword, isPending } = useResetVolunteerPassword();

  // Reset transient state each time the dialog opens for a (new) volunteer.
  useEffect(() => {
    if (open) {
      setUseCustomPassword(false);
      setCustomPassword('');
      setResult(null);
    }
  }, [open, volunteer?.userId]);

  const handleConfirm = () => {
    if (!volunteer) return;
    if (useCustomPassword && !validatePassword(customPassword).isValid) return;

    resetPassword(
      { volunteerId: volunteer.userId, customPassword: useCustomPassword ? customPassword : null },
      {
        onSuccess: (data) => {
          setResult({
            success: true,
            password: useCustomPassword ? customPassword : data.temporaryPassword,
          });
        },
        onError: (error) => {
          setResult({ success: false, error: error.message });
        },
      }
    );
  };

  const customInvalid = useCustomPassword && customPassword.length > 0 && !validatePassword(customPassword).isValid;
  const succeeded = result?.success === true;

  return (
    <Dialog open={open} onOpenChange={(o) => !isPending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            {volunteer ? `${volunteer.firstName} ${volunteer.lastName} · ${volunteer.email}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {!result && (
            <>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="reset-use-custom"
                  checked={useCustomPassword}
                  onCheckedChange={(checked) => {
                    setUseCustomPassword(!!checked);
                    setCustomPassword('');
                  }}
                  disabled={isPending}
                />
                <Label htmlFor="reset-use-custom" className="text-sm font-medium">
                  Set a specific password
                </Label>
              </div>

              {useCustomPassword ? (
                <div className="space-y-1">
                  <Label htmlFor="reset-new-password" className="text-sm font-medium">
                    New Password
                  </Label>
                  <Input
                    id="reset-new-password"
                    type="text"
                    value={customPassword}
                    onChange={(e) => setCustomPassword(e.target.value)}
                    disabled={isPending}
                    autoFocus
                  />
                  <p className={`text-xs ${customInvalid ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {getPasswordHelperText(customPassword)}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  A new temporary password will be generated. Share it securely with the user — it is
                  shown only once, here, after the reset.
                </p>
              )}
            </>
          )}

          {succeeded && (
            <div className="rounded-md border border-green-600/30 bg-green-600/10 p-3 space-y-2">
              <p className="text-sm font-medium text-green-700 dark:text-green-400">
                Password reset successfully.
              </p>
              {result.password && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    {useCustomPassword ? 'New password' : 'Temporary password'} (copy it now):
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded bg-background px-2 py-1 font-mono text-base font-bold">
                      {result.password}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigator.clipboard?.writeText(result.password!)}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {result && !result.success && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
              <p className="text-sm text-destructive">{result.error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {succeeded ? 'Close' : 'Cancel'}
          </Button>
          {!succeeded && (
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={isPending || (useCustomPassword && !validatePassword(customPassword).isValid)}
            >
              {isPending ? 'Resetting...' : 'Reset Password'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
